import React, { useState, useEffect, useRef } from 'react';
import { personalInfo } from '../data/portfolio';

const NAV_ITEMS = [
  { label: 'About',      section: 'about' },
  { label: 'Projects',   section: 'projects' },
  { label: 'Experience', section: 'experience' },
  { label: 'Contact',    section: 'contact' },
];

const Nav = ({ onCommandFocus }) => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Cmd+K / Ctrl+K → focus command interface
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        onCommandFocus?.();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCommandFocus]);

  const scrollTo = (section) => {
    const el = document.getElementById(section);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setMenuOpen(false);
  };

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
      style={{
        backgroundColor: scrolled ? 'rgba(10,10,10,0.92)' : 'transparent',
        backdropFilter: scrolled ? 'blur(12px)' : 'none',
        borderBottom: scrolled ? '1px solid #262626' : '1px solid transparent',
      }}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-14 md:h-16">
          {/* Logo */}
          <button
            onClick={() => scrollTo('hero')}
            className="font-mono text-sm font-medium tracking-widest uppercase"
            style={{ color: 'var(--color-text)', letterSpacing: '0.18em' }}
            aria-label="Scroll to top"
          >
            JAY
          </button>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8" aria-label="Main navigation">
            {NAV_ITEMS.map((item) => (
              <button
                key={item.section}
                onClick={() => scrollTo(item.section)}
                className="nav-link-item"
              >
                {item.label}
              </button>
            ))}
          </nav>

          {/* Right side */}
          <div className="hidden md:flex items-center gap-5">
            {/* Available badge */}
            <div className="flex items-center gap-2">
              <span className="status-dot animate-pulse-dot" aria-hidden="true" />
              <span className="font-mono text-xs" style={{ color: 'var(--color-text2)' }}>
                AVAILABLE
              </span>
            </div>

            {/* Command trigger */}
            <button
              className="cmd-trigger"
              onClick={onCommandFocus}
              aria-label="Open command interface (⌘K)"
            >
              ⌘K
            </button>
          </div>

          {/* Mobile hamburger */}
          <button
            className="md:hidden flex flex-col gap-1.5 p-2"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
          >
            <span
              className="block w-5 h-px transition-all duration-300"
              style={{
                background: 'var(--color-text)',
                transform: menuOpen ? 'rotate(45deg) translateY(5px)' : 'none',
              }}
            />
            <span
              className="block w-5 h-px transition-all duration-300"
              style={{
                background: 'var(--color-text)',
                opacity: menuOpen ? 0 : 1,
              }}
            />
            <span
              className="block w-5 h-px transition-all duration-300"
              style={{
                background: 'var(--color-text)',
                transform: menuOpen ? 'rotate(-45deg) translateY(-5px)' : 'none',
              }}
            />
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className="md:hidden overflow-hidden transition-all duration-300"
        style={{
          maxHeight: menuOpen ? '320px' : '0',
          borderBottom: menuOpen ? '1px solid #262626' : 'none',
          backgroundColor: 'rgba(10,10,10,0.97)',
        }}
      >
        <nav className="px-5 py-6 flex flex-col gap-4" aria-label="Mobile navigation">
          {NAV_ITEMS.map((item) => (
            <button
              key={item.section}
              onClick={() => scrollTo(item.section)}
              className="text-left"
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: '13px',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: 'var(--color-text2)',
              }}
            >
              {item.label}
            </button>
          ))}

          <div className="flex items-center gap-2 pt-2 border-t" style={{ borderColor: 'var(--color-border)' }}>
            <span className="status-dot" />
            <span className="font-mono text-xs" style={{ color: 'var(--color-text2)' }}>
              AVAILABLE FOR WORK
            </span>
          </div>

          <button
            className="cmd-trigger text-left w-fit"
            onClick={() => { onCommandFocus?.(); setMenuOpen(false); }}
          >
            ⌘K — COMMAND
          </button>
        </nav>
      </div>
    </header>
  );
};

export default Nav;
